import _ from 'underscore';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import { SchemaI18n } from './schema-i18n';

const operatorKeyMap = {
  eq: 'Filter_Operator_EQ',
  neq: 'Filter_Operator_NEQ',
  gt: 'Filter_Operator_GT',
  gte: 'Filter_Operator_GTE',
  lt: 'Filter_Operator_LT',
  lte: 'Filter_Operator_LTE',
  in: 'Filter_Operator_Contains',
  contains: 'Filter_Operator_Contains',
  doesnotcontain: 'Filter_Operator_Not_Contains',
  startswith: 'Filter_Operator_StartsWith',
  endswith: 'Filter_Operator_EndsWith',
  containsall: 'FilterOperator_ContainsAll',
  containsany: 'FilterOperator_ContainsAny',
  containsnone: 'FilterOperator_ContainsNone',
};

const operatorStringMap = {
  eq: _TL_('equals'),
  neq: _TL_('does not equal'),
  gt: _TL_('greater than'),
  gte: _TL_('greater than or equals'),
  lt: _TL_('less than'),
  lte: _TL_('less than or equals'),
  in: _TL_('contains'),
  contains: _TL_('contains'),
  doesnotcontain: _TL_('does not contain'),
  startswith: _TL_('begins with'),
  endswith: _TL_('ends with'),
  containsall: _TL_('contains all'),
  containsany: _TL_('contains any'),
  containsnone: _TL_('contains none'),
};

/**
 * The FilterI18n class to do localization for filter
 * It's used to localize date, operator, other values and generate humanized HTML for filter query.
 * @param {Object} schema - FilterSchema only, refer @bingads-webui/filter-schema for more detail.
 * @param {Object} i18n - i18n object, provide getString and formatDecimal functions.
 * @param {Object} [schemaI18n] - optional i18n object to localize property key and enum value in
 * schema.
 * @param {Function} schemaI18n.property - get localized property name string.
 * @param {Function} schemaI18n.value - get localized enum value string.
 * @param {string} skeleton - localization date format string
 * @param {boolean} vnext - indicates whether it's the new version of i18n
 */
export default class FilterI18n {
  constructor(schema, i18n, schemaI18n, skeleton = 'yMd', vnext) {
    this.schema = schema;
    this.i18n = i18n;
    this.schemaI18n = schemaI18n || new SchemaI18n(schema);
    this.skeleton = skeleton;
    this.vnext = vnext;
  }

  /**
   * Localize operator string.
   * @param {string} operator - original operator string
   * @param {object} operatorDisplayNameMap - customized operator convert map
   * @return {string} - localized operator string.
   */
  convertOperator(operator, operatorDisplayNameMap) {
    return operatorDisplayNameMap && operatorDisplayNameMap[operator] ?
      operatorDisplayNameMap[operator] :
      this.i18n.getString(this.vnext ? operatorStringMap[operator] : operatorKeyMap[operator]);
  }

  /**
   * Localize property's value
   * @param {string} value - value of property you want to localize
   * @param {string} propertyKey - the key of property
   * @return {string} - localized value of given property key,
   *                    return value itself if property doesn't exist
   */
  convertValue(value, propertyKey) {
    const property = this.schema.getFilterProperty(propertyKey);
    if (property && property.skipLocalization) {
      return value.toString();
    }
    if (property && property.localize) {
      return property.localize(value);
    }

    if (property && property.type === 'datetime' && value instanceof Date) {
      if (property.hasTime) {
        return this.i18n.formatDate(value, {
          datetime: 'short',
        });
      }
      const utcDate = putil.convertMachineDateToUTCDate(value, this.i18n);
      return this.i18n.formatDate(new Date(utcDate.toISOString()), {
        timeZone: 'UTC',
        skeleton: this.skeleton,
      });
    }

    if (property && property.type === 'number' && property.isPercentValue) {
      return this.i18n.formatPercent(value / 100, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    }

    if (property && property.type === 'number') {
      return this.i18n.formatDecimal(value, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    }

    if (property && property.type === 'integer' && property.isPercentValue) {
      return this.i18n.formatPercent(value / 100, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });
    }

    if (property && property.type === 'integer') {
      return this.i18n.formatDecimal(value, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });
    }

    if (property && property.localized &&
       _.isObject(property.localized) && property.localized[value]) {
      return property.localized[value];
    }
    return this.schemaI18n.value(this.schema.getEntityName(), propertyKey, value);
  }

  /**
   * Localize filterable property's name.
   * @param {string} key - property's key.
   * @return {string} - localized property name, return the key if localized name doesn't exist.
   */
  convertName(key) {
    const property = this.schema.getFilterProperty(key);

    if (property && property.isDeletedProperty) {
      return _TL_('(deleted)');
    }

    return property
      ? this.schemaI18n.property(this.schema.getEntityName(), key)
      : key;
  }
}

